import { useMemo, type ReactNode } from 'react';
import { useIntl, defineMessages } from 'react-intl';

import type { NavItemConfig } from '@atlassian/navigation-common/types';

import { HOME, TEMPLATE_GALLERY } from '@confluence/named-routes/entry-points/routes.ts';

import { type MenuId } from '../PlatformNavigationTypes.ts';
import { platformNavigationIcons } from '../platformNavigationIcons.tsx';
import { DiscoErrorBoundary } from '../components/DiscoErrorBoundary.tsx';

import { useActiveRoute } from './useActiveRoute.ts';
import { useAppsItem } from './useAppsItem.tsx';
import { useSpacesItem } from './useSpacesItem.tsx';

type MenuItemConfigType = Partial<Record<MenuId, NavItemConfig>>;

export const generateMenuLinkConfig = (
	label: string,
	href: string,
	elemBefore: ReactNode = null,
	isSelected: boolean = false,
): NavItemConfig => ({
	type: 'link',
	elemBefore,
	label,
	href,
	isSelected,
	ItemWrapper: DiscoErrorBoundary,
});

// Translations
const messages = defineMessages({
	drafts: {
		id: 'side-navigation.global.drafts',
		defaultMessage: 'Drafts',
		description:
			'Label text for a link to a page that contains links to all content the current user has with the draft / unpublished status',
	},
	tasks: {
		id: 'side-navigation.global.tasks',
		defaultMessage: 'Tasks',
		description:
			'Label text for a link to  Page containing a checklist of tasks assigned to the current user',
	},
	templates: {
		id: 'side-navigation.global.customize.templates',
		defaultMessage: 'Templates',
		description:
			'Label text for an link that takes users to a gallery of page templates they can use to draft formatted content in the product.',
	},
});

// Basic Configuration Hook for the menu items that get sent into GSN
export const useMenuItemsConfig = (): MenuItemConfigType => {
	const { formatMessage } = useIntl();
	const activeRoute = useActiveRoute();
	const spacesItem: NavItemConfig = useSpacesItem();
	const appsItem = useAppsItem();

	return useMemo<MenuItemConfigType>(
		() => ({
			drafts: generateMenuLinkConfig(
				formatMessage(messages.drafts),
				HOME.toUrl({ view: 'drafts' }),
				platformNavigationIcons.drafts,
				activeRoute === 'drafts',
			),
			templates: generateMenuLinkConfig(
				formatMessage(messages.templates),
				TEMPLATE_GALLERY.toUrl(),
				platformNavigationIcons.templates,
				activeRoute === 'templates',
			),
			tasks: generateMenuLinkConfig(
				formatMessage(messages.tasks),
				HOME.toUrl({ view: 'tasks' }),
				platformNavigationIcons.tasks,
				activeRoute === 'tasks',
			),
			spaces: spacesItem,
			apps: appsItem,
		}),
		[formatMessage, activeRoute, spacesItem, appsItem],
	);
};
